import * as Yup from 'yup'

import { EMPTY_LIST } from '../../../constants/common'
import { ROUTE_UI_SECTION_NAME } from '../../../constants/route'
import i18n from '../../../i18n'
import { getItemsIds } from '../../../utils/common'
import { formatDateToApi } from '../../../utils/date'
import { normalizeHtml } from '../../../utils/html'
import { getRequestProfilePathname } from '../../../utils/link'
import { getUserId, isDwellerRole, isUnconfirmed } from '../../../utils/user'

import {
  REQUEST_FORM_FIELD_NAME_APPLICANT,
  REQUEST_FORM_FIELD_NAME_BUILDING,
  REQUEST_FORM_FIELD_NAME_CATEGORY,
  REQUEST_FORM_FIELD_NAME_CHAT_MESSAGE,
  REQUEST_FORM_FIELD_NAME_DATE,
  REQUEST_FORM_FIELD_NAME_DESCRIPTION,
  REQUEST_FORM_FIELD_NAME_EMAIL_MESSAGE,
  REQUEST_FORM_FIELD_NAME_FILES,
  REQUEST_FORM_FIELD_NAME_FLAT,
  REQUEST_FORM_FIELD_NAME_IS_CHATROOM_PUBLIC,
  REQUEST_FORM_FIELD_NAME_MANAGER,
  REQUEST_FORM_FIELD_NAME_RELATED_REQUESTS,
  REQUEST_FORM_FIELD_NAME_TITLE,
  REQUEST_FORM_MANAGERS_FIELD_MAX_ERROR,
  REQUEST_FORM_MANAGERS_FIELD_MAX_ITEMS,
  REQUEST_FORM_REQUIRED_FIELDS,
  REQUEST_FORM_REQUIRED_FIELDS_STAFF,
  REQUEST_FORM_REQUIRED_FIELDS_UNCONFIRMED,
  REQUEST_FORM_TITLE_FIELD_MAX_LENGTH,
} from './RequestForm.constants'

export function isRequestFormManagersFieldMaxError(error) {
  return error === REQUEST_FORM_MANAGERS_FIELD_MAX_ERROR
}

export function getRequestFormValidationSchema() {
  return Yup.object({
    [REQUEST_FORM_FIELD_NAME_APPLICANT]: Yup.object({
      label: Yup.string().nullable(),
      value: Yup.number().nullable(),
    }).nullable(),
    [REQUEST_FORM_FIELD_NAME_BUILDING]: Yup.object().when(
      REQUEST_FORM_FIELD_NAME_APPLICANT,
      {
        is: user => Number.isFinite(user?.value) && isDwellerRole(user),
        then: schema => schema.required(true),
      }
    ),
    [REQUEST_FORM_FIELD_NAME_CATEGORY]: Yup.object({
      name: Yup.string().nullable(),
      value: Yup.number().nullable(),
    }).nullable(),
    [REQUEST_FORM_FIELD_NAME_DATE]: Yup.date().nullable(),
    [REQUEST_FORM_FIELD_NAME_DESCRIPTION]: Yup.string().required(true),
    [REQUEST_FORM_FIELD_NAME_FLAT]: Yup.object().when(
      REQUEST_FORM_FIELD_NAME_APPLICANT,
      {
        is: user => Number.isFinite(user?.value) && isDwellerRole(user),
        then: schema => schema.required(true),
      }
    ),
    [REQUEST_FORM_FIELD_NAME_MANAGER]: Yup.array()
      .of(Yup.object().nullable())
      .max(
        REQUEST_FORM_MANAGERS_FIELD_MAX_ITEMS,
        REQUEST_FORM_MANAGERS_FIELD_MAX_ERROR
      ),
    [REQUEST_FORM_FIELD_NAME_RELATED_REQUESTS]: Yup.array().of(
      Yup.object().nullable()
    ),
    [REQUEST_FORM_FIELD_NAME_TITLE]: Yup.string()
      .max(
        REQUEST_FORM_TITLE_FIELD_MAX_LENGTH,
        i18n.t('Error.MaxLengthKey', {
          limit: REQUEST_FORM_TITLE_FIELD_MAX_LENGTH,
        })
      )
      .required(true),
  })
}

export function getRequestFormCreationPayload({ timezone, user, values }) {
  const files = values[REQUEST_FORM_FIELD_NAME_FILES] ?? EMPTY_LIST

  const payload = {
    [REQUEST_FORM_FIELD_NAME_APPLICANT]:
      values[REQUEST_FORM_FIELD_NAME_APPLICANT]?.value ?? getUserId(user),
    [REQUEST_FORM_FIELD_NAME_BUILDING]:
      values[REQUEST_FORM_FIELD_NAME_BUILDING]?.value ?? null,
    [REQUEST_FORM_FIELD_NAME_CATEGORY]:
      values[REQUEST_FORM_FIELD_NAME_CATEGORY]?.value ?? null,
    [REQUEST_FORM_FIELD_NAME_CHAT_MESSAGE]:
      values[REQUEST_FORM_FIELD_NAME_CHAT_MESSAGE],
    [REQUEST_FORM_FIELD_NAME_EMAIL_MESSAGE]:
      values[REQUEST_FORM_FIELD_NAME_EMAIL_MESSAGE],

    [REQUEST_FORM_FIELD_NAME_FLAT]:
      values[REQUEST_FORM_FIELD_NAME_FLAT]?.value ?? null, // must be `value` instead `number` param cause in this case flat should be created before
    [REQUEST_FORM_FIELD_NAME_IS_CHATROOM_PUBLIC]:
      values[REQUEST_FORM_FIELD_NAME_IS_CHATROOM_PUBLIC],
    [REQUEST_FORM_FIELD_NAME_MANAGER]: getItemsIds(
      values[REQUEST_FORM_FIELD_NAME_MANAGER],
      'value'
    ),
    [REQUEST_FORM_FIELD_NAME_RELATED_REQUESTS]: getItemsIds(
      values[REQUEST_FORM_FIELD_NAME_RELATED_REQUESTS],
      'value'
    ),
    [REQUEST_FORM_FIELD_NAME_TITLE]: values[REQUEST_FORM_FIELD_NAME_TITLE],
  }

  const applicant = values[REQUEST_FORM_FIELD_NAME_APPLICANT]

  if (applicant && isUnconfirmed(applicant)) {
    // unconfirmed users don't have flat id - we have to pass empty property info
    payload[REQUEST_FORM_FIELD_NAME_BUILDING] = null
    payload[REQUEST_FORM_FIELD_NAME_FLAT] = null
  }

  if (values[REQUEST_FORM_FIELD_NAME_DATE]) {
    payload[REQUEST_FORM_FIELD_NAME_DATE] = formatDateToApi(
      values[REQUEST_FORM_FIELD_NAME_DATE],
      timezone
    )
  }

  const description = values[REQUEST_FORM_FIELD_NAME_DESCRIPTION]

  if (description) {
    payload[REQUEST_FORM_FIELD_NAME_DESCRIPTION] = normalizeHtml(description)
  }

  if (files.length > 0) {
    payload[REQUEST_FORM_FIELD_NAME_FILES] = getItemsIds(files)
  }

  return payload
}

export function getRequestFormRequiredFields({
  applicant,
  isStaffUser,
  isUnconfirmed,
}) {
  if (isUnconfirmed) {
    return REQUEST_FORM_REQUIRED_FIELDS_UNCONFIRMED
  }

  if (applicant && isDwellerRole(applicant)) {
    return REQUEST_FORM_REQUIRED_FIELDS
  }

  if (isStaffUser) {
    return REQUEST_FORM_REQUIRED_FIELDS_STAFF
  }

  return REQUEST_FORM_REQUIRED_FIELDS
}

export function getRequestFormSuccessNotification({
  availableSections,
  data,
  t,
}) {
  return data // empty data comes from workflow bot form success
    ? {
        linkProps: {
          // full reload only for the legacy webapp profile
          reloadDocument:
            !availableSections[ROUTE_UI_SECTION_NAME.serviceRequest],
          title: t('Notification.RequestCreatedViewRequestLinkKey'),
          to: getRequestProfilePathname({ availableSections, id: data.id }),
        },
        text: t('Notification.RequestCreatedTextKey', {
          requestNumber: data.request_no,
          title: data.title,
        }),
        title: t('Notification.RequestCreatedTitleKey'),
      }
    : 'Notification.RequestCreatedTitleKey'
}

export function getRequestFormErrorNotification(t) {
  return {
    text: t('Notification.RequestCreationErrorTextKey'),
    title: t('Notification.RequestCreationErrorTitleKey'),
  }
}

export function toRequestFormApplicantSelectOption(value) {
  return {
    label: value,
    value: value.id,
  }
}
